/**
 * LoadingScreen — the full-page glass loading moment.
 *
 * Used at boot (AppRoutes while auth resolves), inside the route Suspense
 * fallback, by Protected/PublicRoute, and as the daily-intro gate's veil.
 * A soft aurora of tenant-brand light drifts behind a frosted glass spinner;
 * the tenant's logo mark breathes above it and a line of copy crossfades
 * every couple of seconds so a slow load never looks frozen.
 *
 * Motion runs through anime.js and cancels on unmount; prefers-reduced-motion
 * gets a static scene with the spinner's own gentle pulse.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { animate } from 'animejs';
import { useBranding } from '../../contexts/ThemeContext';
import { GlassSpinner } from './GlassLoader';
import { getMotionTier, softBlob } from './motionTier';

export const LOADER_LINES = [
  'Loading…',
  'Warming up…',
  'One moment…',
  'Getting things ready…',
  'Almost there…',
  'Fetching the latest…',
  'Setting the scene…',
  'Nearly with you…',
  'Bringing it together…',
  'Just a second…',
  'Opening things up…',
  'Lining things up…',
];

const CYCLE_MS = 2400;

interface LoadingScreenProps {
  /** Fixed copy under the spinner. Omit to rotate through LOADER_LINES. */
  message?: string;
  /** Legacy alias for `message` (kept so existing call sites compile). */
  mesaira?: string;
  size?: 'small' | 'medium' | 'large';
  /** No logo, no copy — just the glass. Used as a quiet veil. */
  quiet?: boolean;
  /** Fill the viewport (default) or just the parent. */
  fullscreen?: boolean;
}

function LoadingScreen({ message, mesaira, size = 'medium', quiet = false, fullscreen = true }: LoadingScreenProps) {
  const fixed = message ?? mesaira;
  const branding = useBrandingSafe();
  const bgRef = useRef<HTMLDivElement>(null);
  const discRef = useRef<HTMLDivElement>(null);
  const logoRef = useRef<HTMLImageElement>(null);
  const lineRef = useRef<HTMLParagraphElement>(null);

  const reducedMotion = useMemo(() => getMotionTier() === 'still', []);

  const spinnerSize = size === 'small' ? 72 : size === 'large' ? 136 : 112;
  const startIndex = useMemo(() => Math.floor(Math.random() * LOADER_LINES.length), []);
  const [lineIndex, setLineIndex] = useState(startIndex);

  // Aurora drift + logo breath.
  useEffect(() => {
    if (reducedMotion) return;
    const anims: Array<{ cancel: () => void }> = [];
    if (bgRef.current) {
      bgRef.current.querySelectorAll('[data-ls-blob]').forEach((b, i) => {
        anims.push(animate(b, {
          translateX: [0, i % 2 === 0 ? 70 : -90, 0],
          translateY: [0, i % 2 === 0 ? -60 : 70, 0],
          scale: [1, 1.15, 1],
          duration: 14000 + i * 3000,
          ease: 'inOutSine',
          loop: true,
        }));
      });
    }
    if (logoRef.current) {
      anims.push(animate(logoRef.current, {
        scale: [1, 1.035, 1],
        duration: 3200,
        ease: 'inOutSine',
        loop: true,
      }));
    }
    return () => anims.forEach((a) => a.cancel());
  }, [reducedMotion]);

  // Entrance: the disc condenses out of blur.
  useEffect(() => {
    if (!discRef.current) return;
    const a = animate(discRef.current, {
      opacity: [0, 1],
      ...(reducedMotion ? {} : { scale: [0.9, 1] }),
      duration: reducedMotion ? 200 : 720,
      ease: 'outExpo',
    });
    return () => { a.cancel(); };
  }, [reducedMotion]);

  // Copy crossfade.
  useEffect(() => {
    if (fixed !== undefined || quiet) return;
    const id = window.setInterval(() => {
      const el = lineRef.current;
      const next = () => setLineIndex((i) => (i + 1) % LOADER_LINES.length);
      if (!el || reducedMotion) { next(); return; }
      animate(el, {
        opacity: [1, 0],
        translateY: [0, -6],
        duration: 260,
        ease: 'inQuad',
        onComplete: () => {
          next();
          animate(el, {
            opacity: [0, 1],
            translateY: [8, 0],
            duration: 420,
            ease: 'outExpo',
          });
        },
      });
    }, CYCLE_MS);
    return () => window.clearInterval(id);
  }, [fixed, quiet, reducedMotion]);

  const logoSrc = branding?.logoUrl || `${process.env.PUBLIC_URL}/brand/logo-mark.svg`;
  const line = fixed ?? LOADER_LINES[lineIndex];

  return (
    <Box
      role="status"
      aria-live="polite"
      aria-label={quiet ? 'Loading' : line}
      sx={{
        position: 'relative',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: fullscreen ? '100vh' : 320,
        width: '100%',
        background: 'var(--surface-subtle, #F8FAFC)',
        color: 'var(--ink, #0F172A)',
      }}
    >
      {/* aurora — quiet brand light, not the intro's full spectrum. Plain
          gradient layers moved by transform only; no filter: blur(). */}
      <Box ref={bgRef} aria-hidden sx={{ position: 'absolute', inset: 0, pointerEvents: 'none', overflow: 'hidden', contain: 'strict' }}>
        <Box data-ls-blob sx={{
          position: 'absolute', width: '64vmax', height: '64vmax', top: '-26%', left: '-20%',
          borderRadius: '50%', opacity: 0.3, willChange: 'transform', transform: 'translateZ(0)',
          background: 'radial-gradient(circle closest-side, var(--brand-primary, #2D5BE3) 0%, color-mix(in srgb, var(--brand-primary, #2D5BE3) 55%, transparent) 40%, transparent 100%)',
        }} />
        <Box data-ls-blob sx={{
          position: 'absolute', width: '56vmax', height: '56vmax', bottom: '-30%', right: '-18%',
          borderRadius: '50%', opacity: 0.26, willChange: 'transform', transform: 'translateZ(0)',
          background: 'radial-gradient(circle closest-side, var(--brand-secondary, #8B5CF6) 0%, color-mix(in srgb, var(--brand-secondary, #8B5CF6) 55%, transparent) 40%, transparent 100%)',
        }} />
        <Box data-ls-blob sx={{
          position: 'absolute', width: '44vmax', height: '44vmax', top: '26%', right: '14%',
          borderRadius: '50%', opacity: 0.16, willChange: 'transform', transform: 'translateZ(0)',
          background: softBlob('#22D3EE'),
        }} />
        <Box sx={{
          position: 'absolute', inset: 0,
          background: 'radial-gradient(ellipse 60% 55% at 50% 50%, color-mix(in srgb, var(--surface, #FFFFFF) 55%, transparent) 0%, transparent 75%)',
        }} />
      </Box>

      <Box ref={discRef} sx={{ position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'center', opacity: 0 }}>
        {!quiet && (
          <Box
            ref={logoRef}
            component="img"
            src={logoSrc}
            alt=""
            aria-hidden
            onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
              const el = e.currentTarget;
              if (!el.src.endsWith('logo512.png')) el.src = `${process.env.PUBLIC_URL}/logo512.png`;
            }}
            sx={{
              width: Math.round(spinnerSize * 0.62),
              height: Math.round(spinnerSize * 0.62),
              maxWidth: '40vw',
              objectFit: 'contain',
              mb: 3.5,
              filter: 'drop-shadow(0 10px 24px rgba(15,23,42,0.12))',
              willChange: 'transform',
            }}
          />
        )}

        <GlassSpinner size={spinnerSize} aria-label={quiet ? 'Loading' : line} />

        {!quiet && (
          <Typography
            ref={lineRef}
            component="p"
            sx={{
              mt: 3.5,
              fontSize: 14.5,
              fontWeight: 500,
              letterSpacing: '0.01em',
              color: 'var(--ink-muted, #475569)',
              textAlign: 'center',
              maxWidth: 320,
              minHeight: 22,
              willChange: 'transform, opacity, filter',
            }}
          >
            {line}
          </Typography>
        )}
      </Box>
    </Box>
  );
}

/** useBranding throws outside BrandingProvider; the loader must never. */
function useBrandingSafe(): { logoUrl?: string } | null {
  try {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    return useBranding()?.branding ?? null;
  } catch {
    return null;
  }
}

export default LoadingScreen;
